
import { reactive, computed, onMounted, watch, ref, defineComponent } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'
import Tab from '@/components/elements/Tab.vue'
import HospitalLabel from '@/components/elements/HospitalLabel.vue'
import HospitalContactBtn from '@/components/block/HospitalContactBtn.vue'
import HospitalCassette from '@/components/block/HospitalCassette.vue'
import HospitalProgramCassette from '@/components/block/HospitalProgramCassette.vue'
import HospitalsDetailTabAbout from '@/components/block/HospitalsDetailTabAbout.vue'
import HospitalsDetailTabEarly from '@/components/block/HospitalsDetailTabEarly.vue'
import HospitalsDetailTabLatterDetail from '@/components/block/HospitalsDetailTabLatterDetail.vue'
import HospitalsDetailTabLatterList from '@/components/block/HospitalsDetailTabLatterList.vue'
import HospitalsDetailTabEvents from '@/components/block/HospitalsDetailTabEvents.vue'
import HospitalsDetailAbstract from '@/components/block/HospitalsDetailAbstract.vue'
import { axiosSetting } from '@/utils/axiosSetting'
import { setDetailDocumentTitle } from '@/utils/hospitals/setDetailDocumentTitle'
import smoothscroll from 'smoothscroll-polyfill'
import { Hospital, Specialist, SpecialistCassete } from '@/types/HospitalDetail'
import { ActionHistory } from '@/types/StoreState'
import { getDepartmentsData } from '@/utils/hospitals/getDepartmentsData'

export default defineComponent({
  name: 'hospitalsDetail',

  components: {
    Tab,
    HospitalLabel,
    HospitalContactBtn,
    HospitalCassette,
    HospitalProgramCassette,
    HospitalsDetailTabAbout,
    HospitalsDetailTabEarly,
    HospitalsDetailTabLatterList,
    HospitalsDetailTabLatterDetail,
    HospitalsDetailTabEvents,
    HospitalsDetailAbstract
  },

  props: {
    detail: {
      type: Object,
      default: () => ({})
    }
  },

  beforeRouteEnter: async (to: any, from: any, next: any) => {
    const getDetail = axiosSetting('app', 'page')
    const data = await getDetail
      .get(`/api/v1/detail?hospital_id=${to.params.id}`)
      .then((res) => res.data)
      .catch(() => 'error')

    if (data === 'error') return next('/500')
    if (!data.hospital_name) return next('/404')

    if (to.meta.breadCrumb.length <= 1) {
      to.meta.breadCrumb.push({ label: data.hospital_name })
    } else {
      to.meta.breadCrumb[1] = { label: data.hospital_name }
    }

    setDetailDocumentTitle(data)

    to.matched[0].props.default = {
      detail: data
    }

    next()
  },

  beforeRouteUpdate: async (to: any) => {
    const getDetail = axiosSetting('app', 'page')
    const data = await getDetail
      .get(`/api/v1/detail?hospital_id=${to.params.id}`)
      .then((res) => res.data)

    if (to.meta.breadCrumb.length <= 1) {
      to.meta.breadCrumb.push({ label: data.hospital_name })
    } else {
      to.meta.breadCrumb[1] = { label: data.hospital_name }
    }

    setDetailDocumentTitle(data)

    to.matched[0].props.default = {
      detail: data
    }
  },

  setup(props) {
    smoothscroll.polyfill()
    const route = useRoute()
    const state = reactive({
      fixed: false,
      hiddenTab: false,
      showPrevBtn: false,
      currentTab: 0
    })
    const store = useStore()
    // 各イベントに参加している全病院ID
    const eventHospitalIds = store.getters['eventHospitalIds']
    const isParticipateEvent = store.getters['isParticipateEvent'](
      `${route.params.id}`
    )

    const tab = computed(() => {
      const list = [
        {
          label: '病院基本情報',
          name: ''
        }
      ]
      if (props.detail.training_id) {
        list.push({
          label: '初期研修情報',
          name: 'early'
        })
      }
      if (props.detail.specialist?.length) {
        list.push({
          label: '専門研修',
          name: 'latter'
        })
      }
      if (isParticipateEvent) {
        list.push({
          label: '参加イベント',
          name: 'events'
        })
      }
      return list
    })

    const judgeCurrentTab = () => {
      const index = tab.value.findIndex((item) => item.name == route.query.tab)
      return index > 0 ? index : 0
    }

    const currentlatterPage = ref('')
    const currentlatterDetailId = ref('')

    const setCurrentlatterPage = (tab: string, id: string) => {
      if (tab !== 'latter') {
        currentlatterPage.value = ''
      } else if (id) {
        currentlatterPage.value = 'detail'
        currentlatterDetailId.value = id
      } else {
        currentlatterPage.value = 'list'
      }
    }
    setCurrentlatterPage(route.query.tab as string, route.query.id as string)

    const currentlatterDetailData = computed(
      (): Specialist => {
        return props.detail.specialist.find(
          (item: Specialist) =>
            String(item.s_training_id) === currentlatterDetailId.value
        )
      }
    )

    // タブの位置にスクロール
    const moveTabHeader = (smooth = false) => {
      const cnt = document.getElementById('hospitalsDetailCnt')
      if (!cnt) return

      const cntYOffset = window.pageYOffset + cnt.getBoundingClientRect().top

      if (window.scrollY > cntYOffset || smooth) {
        const behavior = smooth ? 'smooth' : 'auto'
        window.scrollTo({
          top: cntYOffset,
          behavior: behavior
        })
      }
    }

    const latterDetailLocalNav: any = ref([])
    const getLatterDetailLocalNav = (nav: { id: string; label: string }[]) => {
      latterDetailLocalNav.value = nav
    }

    const hasRecruitData = computed(
      () =>
        props.detail.qualification ||
        props.detail.test_day ||
        props.detail.selection_process ||
        props.detail.application_documents ||
        props.detail.positions_num ||
        props.detail.employees_num ||
        props.detail.applicants_num ||
        props.detail.j_graduating_school ||
        props.detail.hospital_tour_date ||
        props.detail.hospital_tour_subsidy
    )

    const localNav = computed(() => {
      if (tab.value[state.currentTab].name === '') {
        return [
          { id: 'about', label: '概要' },
          {
            id: 'basicinfo1',
            label:
              props.detail.basicinfo1_title && props.detail.basicinfo1_body
                ? props.detail.basicinfo1_title
                : ''
          },
          {
            id: 'deptInfo',
            label:
              props.detail.dept_info &&
              JSON.parse(props.detail.dept_info).length > 0
                ? '診療科情報'
                : ''
          },
          { id: 'feature', label: '特徴' },
          {
            id: 'basicinfo2',
            label:
              props.detail.basicinfo2_title && props.detail.basicinfo2_body
                ? props.detail.basicinfo2_title
                : ''
          },
          { id: 'access', label: 'アクセス' },
          {
            id: 'basicinfo3',
            label:
              props.detail.basicinfo3_title && props.detail.basicinfo3_body
                ? props.detail.basicinfo3_title
                : ''
          }
        ]
      }
      if (tab.value[state.currentTab].name === 'early') {
        return [
          {
            id: 'traininginfo1',
            label:
              props.detail.traininginfo1_title &&
              props.detail.traininginfo1_body
                ? props.detail.traininginfo1_title
                : ''
          },
          {
            id: 'schedule',
            label:
              !!props.detail.hospital_training_program &&
              props.detail.hospital_training_program.length > 0
                ? '臨床研修プログラム'
                : ''
          },

          {
            id: 'recruit',
            label: hasRecruitData.value ? '採用試験・病院見学' : ''
          },
          { id: 'salary', label: '給与・勤務体系・福利厚生' },

          {
            id: 'traininginfo2',
            label:
              props.detail.traininginfo2_title &&
              props.detail.traininginfo2_body
                ? props.detail.traininginfo2_title
                : ''
          },
          { id: 'contact', label: 'お問い合わせ・お申し込み' },
          {
            id: 'traininginfo3',
            label:
              props.detail.traininginfo3_title &&
              props.detail.traininginfo3_body
                ? props.detail.traininginfo3_title
                : ''
          },
          {
            id: 'traininginfo4',
            label:
              props.detail.traininginfo4_title &&
              props.detail.traininginfo4_body
                ? props.detail.traininginfo4_title
                : ''
          }
        ]
      }
      if (currentlatterPage.value === 'detail') {
        return latterDetailLocalNav.value
      }
      return []
    })

    const features = computed(() => {
      const currentkeywords = props.detail.discerning_conditions
        ? props.detail.discerning_conditions
        : []

      return store.state.master.keywords.map((el: any) => {
        return {
          label: el.option_name,
          disabled: currentkeywords.indexOf(el.option_name) === -1
        }
      })
    })

    const labels = computed(() => {
      const res = []
      if (props.detail.prefectures) res.push(props.detail.prefectures)
      if (props.detail.training_id) res.push('初期研修')
      if (props.detail.specialist.length) res.push('専門研修')
      return res
    })

    // おすすめ病院・専門研修
    // ---------------------------

    const ajax = axiosSetting('app', 'block')
    const initializedRecommendHospitals = ref(false)
    const recommendHospitals: Hospital[] = reactive([])
    const recommendPrograms: SpecialistCassete[] = reactive([])

    const getRecommendHospitals = () => {
      const url = `/api/v1/j_search/recommend?prefectures=${props.detail.prefectures_code}`
      ajax.get<Hospital[]>(url).then((res) => {
        recommendHospitals.splice(0)
        res.data.forEach((item: Hospital) => {
          if (item.hospital_id === Number(route.params.id)) return
          if (recommendHospitals.length >= 3) return
          recommendHospitals.push(item)
        })
        initializedRecommendHospitals.value = true
      })
    }
    const getRecommendPrograms = (sId: string) => {
      const url = `/api/v1/j_search/specialist_near_area?s_training_id=${sId}`
      ajax.get<SpecialistCassete[]>(url).then((res) => {
        recommendPrograms.splice(0)
        res.data.forEach((item: SpecialistCassete) => {
          if (recommendPrograms.length >= 3) return
          recommendPrograms.push(item)
        })
      })
    }

    const hasRecommend = computed(() => {
      return (
        (currentlatterPage.value === 'detail' && recommendPrograms.length) ||
        (currentlatterPage.value !== 'detail' && recommendHospitals.length)
      )
    })

    const getRecommend = () => {
      if (currentlatterPage.value === 'detail') {
        getRecommendPrograms(currentlatterDetailId.value)
      } else {
        getRecommendHospitals()
      }
    }

    // お気に入り関連
    // ---------------------------

    // お気に入りに追加済みかどうか
    const isFavoriteHospital = computed(() => {
      if (!store.state.isLogin) return false

      return store.state.userInfo.favorite_history.some(
        (el: ActionHistory) => el.hospital_id === Number(route.params.id)
      )
    })

    // お気に入りの追加・削除
    const toggleFavorite = () => {
      if (isFavoriteHospital.value) {
        store.dispatch('removeFavorite', { id: Number(route.params.id) })
      } else {
        store.dispatch('addFavorite', { id: Number(route.params.id) })
      }
    }

    // イベント関連
    // ---------------------------

    // イベント参加病院かどうか判定
    const setEventStatus = async () => {
      if (!eventHospitalIds) return

      // イベント参加病院であればイベント情報を取得する
      if (!isParticipateEvent) return

      if (route.query.tab === 'events') {
        state.currentTab = tab.value.findIndex((item) => item.name == 'events')
      }

      // 全イベント取得
      await store.dispatch(
        'events/loadEvents',
        { perPage: 100 },
        { root: true }
      )
      // 全イベントを全病院idでソート
      store.commit('events/setEventsByHospitalIds', eventHospitalIds)

      // この病院が参加しているイベントを取得
      // store.getters['events/getEventsByHospitalId'](route.params.id)の値を直接 return
    }

    // タブ遷移 / 専門研修遷移関連
    // ---------------------------

    // タブ操作
    const clickTab = (i: number, smooth = false) => {
      state.currentTab = i
      const id = route.params.id as string
      const q = i === 0 ? id : `${id}?tab=${tab.value[i].name}`
      history.replaceState(null, '', q)

      setCurrentlatterPage(tab.value[i].name, '')
      moveTabHeader(smooth)
      setDetailDocumentTitle(props.detail)

      if (!initializedRecommendHospitals.value) getRecommendHospitals()
    }

    const showLatterList = () => {
      const latterTab = tab.value.findIndex((item) => item.name == 'latter')
      clickTab(latterTab)
    }
    const showEventTab = () => {
      const eventTab = tab.value.findIndex((item) => item.name == 'events')
      if (!eventTab) return
      clickTab(eventTab, true)
    }

    const showLatterDetail = (s_training_id: number) => {
      state.currentTab = tab.value.findIndex((item) => item.name == 'latter')
      const id = route.params.id as string
      const q = `${id}?tab=latter&id=${s_training_id}`
      history.replaceState(null, '', q)
      setCurrentlatterPage('latter', String(s_training_id))
      setDetailDocumentTitle(props.detail)
      moveTabHeader()
      getRecommendPrograms(String(s_training_id))
    }

    // 戻るの制御
    // ---------------------------
    const showPrevBtn = () => {
      const referrer = document.referrer
      if (referrer && history.state.back) {
        const domain = window.location.protocol + '//' + window.location.host
        if (referrer.indexOf(domain) === 0) {
          state.showPrevBtn = true
        }
      }
    }

    const pageBack = () => window.history.back()

    // ページの描画と更新時に実行したい処理
    const pageInit = () => {
      getRecommend()
      setEventStatus()
      showPrevBtn()
      state.currentTab = judgeCurrentTab()
    }
    pageInit()

    // ページが更新された時に実行
    watch(
      () => route.params.id,
      () => {
        if (route.name === 'HospitalsDetail') {
          pageInit()
        }
      }
    )

    watch(
      () => store.state.eventParticipatingHospitals,
      (value) => {
        if (!value) return
        setEventStatus()
      }
    )

    // コンテンツのアンカーリンク
    const scrollContents = (anchor: string, e: any) => {
      e.preventDefault()

      const el = document.getElementById(anchor)

      if (el) {
        const pos = el.offsetTop - 110

        window.scrollTo({
          top: pos,
          behavior: 'smooth'
        })
      }
    }

    onMounted(async () => {
      // メニューがposition: sticky で固定された時にclassを付与
      const menu = document.getElementById('hospitalsDetailMenuWrap')
      const menuBefore = document.getElementById('menu-before')
      if (menu && menuBefore) {
        const observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              menu.classList.remove('is-fixed')
            } else {
              menu.classList.add('is-fixed')
            }
          }
        })
        observer.observe(menuBefore)
      }
    })

    const departmentsData = computed(() => getDepartmentsData(props.detail))

    return {
      state,
      tab,
      localNav,
      labels,
      features,
      isParticipateEvent,
      isFavoriteHospital,
      currentlatterPage,
      currentlatterDetailData,
      clickTab,
      showEventTab,
      showLatterList,
      showLatterDetail,
      getLatterDetailLocalNav,
      pageBack,
      toggleFavorite,
      scrollContents,
      recommendHospitals,
      recommendPrograms,
      hasRecommend,
      eventList: computed(() =>
        store.getters['events/getEventsByHospitalId'](route.params.id)
      ),
      departmentsData
    }
  }
})
